// Client-side tracking of the signed-in token's lifetime.
//
// The API answers an expired token with { success: false, message: 'jwt expired' } on every
// authed call, but screens that load lists turn that into an empty list, so a user who leaves
// the page open past expiry sees a hollow home or profile rather than a clear state. The token
// itself carries its expiry (`exp`), so this module reads it when the token is stored, warns
// shortly before the deadline, and flips the whole app to the expired screen at the deadline
// or on the first expired API response, whichever comes first.
//
// Events (on window):
//   EXPIRING_EVENT  fired when the token is within WARN_BEFORE_MS of expiry (may repeat)
//   EXPIRED_EVENT   fired once per page load when the session is over
//
// Timers are throttled in background tabs, so the deadline is re-checked on focus and when
// the tab becomes visible again.
import { getItem, setItem } from '../Storage';

const EXPIRING_EVENT = 'tt:session-expiring';
const EXPIRED_EVENT  = 'tt:session-expired';
const WARN_BEFORE_MS = 5 * 60 * 1000;
const MAX_TIMEOUT_MS = 2147483647; // setTimeout overflows above this (~24.8 days)

let expired_fired    = false;
let warn_timer       = null;
let expiry_timer     = null;
let window_listeners = false;

const decode_payload = (token) => {
  if (!token || typeof token !== 'string') { return null; }

  let parts = token.split('.');
  if (parts.length < 2) { return null; }

  try {
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    let pad    = base64.length % 4 ? '='.repeat(4 - (base64.length % 4)) : '';
    let json   = window.atob(base64 + pad);
    return JSON.parse(json);
  } catch (err) {
    return null;
  }
}

// Expiry of a token in epoch milliseconds, or null when the token carries none.
const get_expiry_ms = (token) => {
  let payload = decode_payload(token);
  return payload && typeof payload.exp === 'number' ? payload.exp * 1000 : null;
}

// Milliseconds left on the stored token, or null when there is no token or no expiry.
const get_remaining_ms = () => {
  let expiry = get_expiry_ms(getItem('token'));
  return expiry === null ? null : expiry - Date.now();
}

const clear_timers = () => {
  if (warn_timer)   { clearTimeout(warn_timer);   warn_timer   = null; }
  if (expiry_timer) { clearTimeout(expiry_timer); expiry_timer = null; }
}

const dispatch = (name) => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') { return; }
  window.dispatchEvent(new CustomEvent(name));
}

const clear_session = () => {
  setItem('token', '');
  setItem('user_id', '');
}

// Ends the session: clears the stored credentials and tells the app shell, once per page load.
const mark_session_expired = () => {
  if (expired_fired) { return; }
  expired_fired = true;
  clear_timers();
  clear_session();
  dispatch(EXPIRED_EVENT);
}

// Arms the warning and expiry timers for a token that was just stored or re-read. A token that
// is already past its expiry ends the session immediately.
const arm = (token) => {
  clear_timers();

  let expiry = get_expiry_ms(token);
  if (expiry === null) { return; }

  expired_fired = false;

  let remaining = expiry - Date.now();
  if (remaining <= 0) {
    mark_session_expired();
    return;
  }

  let warn_in = remaining - WARN_BEFORE_MS;
  if (warn_in <= 0) {
    dispatch(EXPIRING_EVENT);
  } else {
    warn_timer = setTimeout(() => { dispatch(EXPIRING_EVENT); }, Math.min(warn_in, MAX_TIMEOUT_MS));
  }

  // check_now re-arms when the delay had to be capped and ends the session otherwise.
  expiry_timer = setTimeout(check_now, Math.min(remaining, MAX_TIMEOUT_MS));
}

// Compares the stored token with the clock. Used by the expiry timer and whenever the tab
// regains focus, since a throttled timer may fire late or not at all.
const check_now = () => {
  let token = getItem('token');
  if (!token) { return; }
  arm(token);
}

// Run once on app boot. A token left in storage from before a reload that has already expired
// is dropped quietly, so the home screen starts from its signed-out state (or, for a partner
// link, the fresh session overwrites it) instead of the expired screen appearing on load.
const init_from_storage = () => {
  let token = getItem('token');
  if (!token) { return; }

  let expiry = get_expiry_ms(token);
  if (expiry !== null && expiry <= Date.now()) {
    clear_session();
    return;
  }

  arm(token);
}

const bind_window_listeners = () => {
  if (window_listeners || typeof window === 'undefined') { return; }
  window_listeners = true;

  window.addEventListener('focus', check_now);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') { check_now(); }
  });
}

// Called by the API layer when a response says the token has expired.
const handle_expired_response = () => {
  mark_session_expired();
}

const SessionExpiry = {
  EXPIRING_EVENT,
  EXPIRED_EVENT,
  WARN_BEFORE_MS,
  get_expiry_ms,
  get_remaining_ms,
  arm,
  check_now,
  init_from_storage,
  bind_window_listeners,
  handle_expired_response,
  mark_session_expired
}

export default SessionExpiry;
